import { useToast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { Timestamp } from "firebase/firestore";
import { CalendarIcon } from "lucide-react";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn, timestampToDate } from "@/lib/utils";
import { updateTrial } from "@/services/firebase";
import { Trial } from "@/types/trial";
import CropSelector from "@/components/CropSelector";
import LocationSelector from "@/components/LocationSelector";
import { useCrops, useLocations } from "@/hooks/useFirestore";
import { useAuth } from "@/contexts/AuthContext";
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard";

const trialSchema = z
  .object({
    name: z.string().min(1, { message: "Trial name is required" }),
    description: z.string().optional(),
    location: z.string().optional(),
    // Required, and deliberately left EMPTY rather than defaulted for a trial whose
    // stored startDate is missing/unusable. Defaulting to today would let someone
    // saving an unrelated edit silently fabricate a start date on a research record
    // — `onSubmit` writes startDate unconditionally — and could even land it after
    // the existing endDate. Zod blocks the save until a human picks one.
    startDate: z.date(),
    endDate: z.date().optional().nullable(),
    crops: z.array(z.string()).optional(),
  })
  .refine((data) => (data.startDate && data.endDate ? data.endDate >= data.startDate : true), {
    message: "End date must be after start date",
    path: ["endDate"],
  });

interface EditTrialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trial: Trial;
}

const EditTrialDialog: React.FC<EditTrialDialogProps> = ({ open, onOpenChange, trial }) => {
  const { toast } = useToast();
  const { userRole } = useAuth();
  const { crops, loading: cropsLoading } = useCrops();
  const { locations, loading: locationsLoading } = useLocations();

  // Only editors and admins can add new crops/locations
  const canEdit = userRole && ["editor", "admin"].includes(userRole);

  // Compute initial orphan crops (crops in trial but not in org's crops list)
  const initialOrphanCrops = useMemo(() => {
    if (cropsLoading) return [];
    return (trial.crops || []).filter(
      cropName => !crops.some(c => c.name === cropName)
    );
  }, [trial.crops, crops, cropsLoading]);

  // Compute initial orphan location (location in trial but not in org's locations list)
  const initialOrphanLocation = useMemo(() => {
    if (locationsLoading || !trial.locationName) return undefined;
    return !locations.some(l => l.name === trial.locationName) ? trial.locationName : undefined;
  }, [trial.locationName, locations, locationsLoading]);

  const form = useForm<z.infer<typeof trialSchema>>({
    resolver: zodResolver(trialSchema),
    defaultValues: {
      name: trial.name,
      // `?? ""` for the same reason as the two fields below it: `description` is
      // `string | null`, and the schema's `z.string().optional()` accepts
      // undefined but not null — a stored null failed validation with
      // "Expected string, received null" and blocked saving an unrelated edit.
      description: trial.description ?? "",
      location: trial.locationName || "",
      startDate: timestampToDate(trial.startDate),
      endDate: timestampToDate(trial.endDate) ?? null,
      crops: trial.crops || [],
    },
  });

  // Reset to the trial's values whenever the dialog opens. EditTrialDialog can
  // stay mounted between open/close cycles (e.g. on TrialDetails), so without
  // this an accepted "Discard changes" would leave the edited values in the
  // form — they'd reappear on reopen and keep formState.isDirty true.
  useEffect(() => {
    if (open) {
      form.reset({
        name: trial.name,
        description: trial.description ?? "",
        location: trial.locationName || "",
        startDate: timestampToDate(trial.startDate),
        endDate: timestampToDate(trial.endDate) ?? null,
        crops: trial.crops || [],
      });
    }
    // Reset only on open transitions; depending on `trial`/`form` would clobber
    // in-progress edits if the live trial snapshot changes while the dialog is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onSubmit = async (data: z.infer<typeof trialSchema>) => {
    const updateData: Partial<Trial> = {
      name: data.name,
      description: data.description,
      locationName: data.location,
      crops: data.crops,
      startDate: Timestamp.fromDate(data.startDate),
      endDate: data.endDate ? Timestamp.fromDate(data.endDate) : undefined,
    };

    await updateTrial(trial.id, updateData);

    toast({
      title: "Trial updated",
      description: `${data.name} has been updated successfully`,
    });

    onOpenChange(false);
  };

  // Warn before discarding in-progress trial edits (Escape, click-outside, X, Cancel).
  const guard = useUnsavedChangesGuard({ isDirty: form.formState.isDirty, onOpenChange });

  return (
    <>
    <Dialog
      open={open}
      onOpenChange={guard.guardedOnOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Trial</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Trial Name*</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      className="min-h-[80px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <FormControl>
                    <LocationSelector
                      locations={locations}
                      locationsLoading={locationsLoading}
                      selectedLocation={field.value || ""}
                      onSelectLocation={field.onChange}
                      canAddLocation={canEdit}
                      placeholder="Select a location"
                      initialOrphanLocation={initialOrphanLocation}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="crops"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Crop Type</FormLabel>
                  <FormControl>
                    <CropSelector
                      crops={crops}
                      cropsLoading={cropsLoading}
                      selectedCrops={field.value || []}
                      onToggleCrop={(cropName) => {
                        const currentCrops = field.value || [];
                        const newCrops = currentCrops.includes(cropName)
                          ? currentCrops.filter(c => c !== cropName)
                          : [...currentCrops, cropName];
                        field.onChange(newCrops);
                      }}
                      canAddCrop={canEdit}
                      initialOrphanCrops={initialOrphanCrops}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Start Date*</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn("pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                            {field.value ? format(field.value, "PPP") : <span>Pick a date</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0"
                        align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>End Date</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn("pl-3 text-left font-normal", !field.value && "text-muted-foreground")}>
                            {field.value ? format(field.value, "PPP") : <span>Optional</span>}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent
                        className="w-auto p-0"
                        align="start">
                        <Calendar
                          mode="single"
                          selected={field.value || undefined}
                          onSelect={field.onChange}
                          disabled={(date) => {
                            const startDate = form.getValues("startDate");
                            return startDate ? date < startDate : false;
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={guard.requestClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-agri-green hover:bg-agri-green-dark">
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>

    {guard.confirmation}
    </>
  );
};

export default EditTrialDialog;
